import { Skeleton, Tooltip } from 'antd'
import { t } from 'i18next'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import InfoIcon from '@/assets/icons/info-i.svg?react'
import { Trans } from '@/components'
import { ButtonRadio, ButtonRadioItem } from '@/components/base/ButtonRadio'
import { Dropdown } from '@/components/base/Dropdown'
import { EmptyView } from '@/components/EmptyView'
import { TeamRole } from '@/consts/team'
import { useTeamStore } from '@/stores/team'

import {
  MemberResourceStat,
  ResourceType,
  useTeamMemberResourceStats,
} from '../hooks/useTeamMemberResourceStats'

const getResourceTypes = (): ButtonRadioItem<ResourceType>[] => [
  { label: t('teamHome.admin.resources.typeInstance', 'Instance'), value: 'instance' },
  { label: t('teamHome.admin.resources.typeSnapshot', 'Snapshot'), value: 'snapshot' },
  { label: t('teamHome.admin.resources.typeDatadrive', 'Datadrive'), value: 'datadrive' },
]

// Resource navigation links
const getResourceLinks = (): Record<ResourceType, { label: string; path: string }> => ({
  instance: {
    label: t('teamHome.admin.resources.instancesLink', 'Instances'),
    path: '/team/instances',
  },
  snapshot: {
    label: t('teamHome.admin.resources.snapshotsLink', 'Snapshots'),
    path: '/team/snapshots/admin',
  },
  datadrive: {
    label: t('teamHome.admin.resources.datadriveLink', 'Datadrive'),
    path: '/team/data/admin',
  },
})

// Dynamic sort options based on resource type
const getSortOptions = (): Record<ResourceType, Array<{ label: string; value: string }>> => ({
  instance: [
    {
      label: t('teamHome.admin.resources.sortInstanceNumber', 'Instance Number'),
      value: 'instanceNumber',
    },
    { label: t('teamHome.admin.resources.sortCost', 'Cost'), value: 'cost' },
    { label: t('teamHome.admin.resources.sortDuration', 'Duration'), value: 'duration' },
  ],
  snapshot: [
    {
      label: t('teamHome.admin.resources.sortSnapshotNumber', 'Snapshot Number'),
      value: 'snapshotNumber',
    },
    { label: t('teamHome.admin.resources.sortStorage', 'Storage'), value: 'storage' },
    { label: t('teamHome.admin.resources.sortPercentage', 'Percentage'), value: 'percentage' },
  ],
  datadrive: [
    { label: t('teamHome.admin.resources.sortStorage', 'Storage'), value: 'storage' },
    { label: t('teamHome.admin.resources.sortPercentage', 'Percentage'), value: 'percentage' },
  ],
})

export function TeamResourcesTable() {
  const { t: tLocal } = useTranslation()
  const [selectedType, setSelectedType] = useState<ResourceType>('instance')
  const [sortBy, setSortBy] = useState('instanceNumber')
  const teamStore = useTeamStore()
  const teamResID = teamStore.teamTokenData?.teamResID

  // Helper function to get translated role label
  const getRoleLabel = (role: TeamRole): string => {
    if (role === TeamRole.Owner) return tLocal('team.role.owner')
    if (role === TeamRole.Admin) return tLocal('team.role.admin')
    return tLocal('team.role.member')
  }

  const { data, loading } = useTeamMemberResourceStats({
    teamResID,
    resourceType: selectedType,
  })

  const RESOURCE_TYPES = getResourceTypes()
  const RESOURCE_LINKS = getResourceLinks()
  const SORT_OPTIONS = getSortOptions()

  // Handle resource type change - reset sort to first option
  const handleTypeChange = (value: ResourceType) => {
    setSelectedType(value)
    setSortBy(SORT_OPTIONS[value][0].value)
  }

  // Sort and get top 5 members
  const sortedTop5Members = useMemo(() => {
    if (!data || data.length === 0) return []

    const sorted = [...data].sort((a, b) => {
      if (selectedType === 'instance') {
        if (sortBy === 'instanceNumber') {
          return (b.instanceNumber || 0) - (a.instanceNumber || 0) // Descending
        } else if (sortBy === 'cost') {
          // Extract numeric value from "XX Credit" string
          const costA = parseFloat(a.cost?.replace(' Credit', '') || '0')
          const costB = parseFloat(b.cost?.replace(' Credit', '') || '0')
          return costB - costA // Descending
        } else if (sortBy === 'duration') {
          // Extract numeric value from "XX Hours" string
          const durationA = parseFloat(a.duration?.replace(' Hours', '') || '0')
          const durationB = parseFloat(b.duration?.replace(' Hours', '') || '0')
          return durationB - durationA // Descending
        }
      } else if (selectedType === 'snapshot') {
        if (sortBy === 'snapshotNumber') {
          return (b.snapshotNumber || 0) - (a.snapshotNumber || 0) // Descending
        } else if (sortBy === 'storage') {
          // Extract numeric value from "XX GB" string
          const storageA = parseFloat(a.storageQuota?.replace(' GB', '') || '0')
          const storageB = parseFloat(b.storageQuota?.replace(' GB', '') || '0')
          return storageB - storageA // Descending
        } else if (sortBy === 'percentage') {
          // Extract numeric value from "XX%" string
          const percentA = parseFloat(a.percentage?.replace('%', '') || '0')
          const percentB = parseFloat(b.percentage?.replace('%', '') || '0')
          return percentB - percentA // Descending
        }
      } else if (selectedType === 'datadrive') {
        if (sortBy === 'storage') {
          // Extract numeric value from "XX GB" string
          const storageA = parseFloat(a.storageQuota?.replace(' GB', '') || '0')
          const storageB = parseFloat(b.storageQuota?.replace(' GB', '') || '0')
          return storageB - storageA // Descending
        } else if (sortBy === 'percentage') {
          // Extract numeric value from "XX%" string
          const percentA = parseFloat(a.percentage?.replace('%', '') || '0')
          const percentB = parseFloat(b.percentage?.replace('%', '') || '0')
          return percentB - percentA // Descending
        }
      }
      return 0
    })

    // Get top 5 and add rank
    return sorted.slice(0, 5).map((member, index) => ({
      ...member,
      rank: String(index + 1).padStart(2, '0'),
    }))
  }, [data, selectedType, sortBy])

  // Dynamic columns based on resource type
  const renderTableHeaders = () => {
    if (selectedType === 'instance') {
      return (
        <tr className="text-secondary">
          <th className="text-left py-3 px-4 t-sm rounded-l-lg">
            <Trans i18nKey="teamHome.admin.resources.table.rank">#</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.id">ID</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.name">Name</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.role">Role</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.instanceNumber">Instance Number</Trans>
          </th>
          <th className="text-right py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.cost">Cost</Trans>
          </th>
          <th className="text-right py-3 px-4 t-sm  rounded-r-lg">
            <Trans i18nKey="teamHome.admin.resources.table.duration">Duration</Trans>
          </th>
        </tr>
      )
    } else if (selectedType === 'snapshot') {
      return (
        <tr className="text-secondary">
          <th className="text-left py-3 px-4 t-sm  rounded-l-lg">
            <Trans i18nKey="teamHome.admin.resources.table.rank">#</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.id">ID</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.name">Name</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.role">Role</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.snapshotNumber">Snapshot Number</Trans>
          </th>
          <th className="text-right py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.storage">Storage</Trans>
          </th>
          <th className="text-right py-3 px-4 t-sm  rounded-r-lg">
            <Trans i18nKey="teamHome.admin.resources.table.percentage">Percentage</Trans>
          </th>
        </tr>
      )
    } else {
      // datadrive
      return (
        <tr className="text-secondary">
          <th className="text-left py-3 px-4 t-sm  rounded-l-lg">
            <Trans i18nKey="teamHome.admin.resources.table.rank">#</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.id">ID</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.name">Name</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.role">Role</Trans>
          </th>
          <th className="text-left py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.region">Region</Trans>
          </th>
          <th className="text-right py-3 px-4 t-sm ">
            <Trans i18nKey="teamHome.admin.resources.table.storage">Storage</Trans>
          </th>
          <th className="text-right py-3 px-4 t-sm  rounded-r-lg">
            <Trans i18nKey="teamHome.admin.resources.table.percentage">Percentage</Trans>
          </th>
        </tr>
      )
    }
  }

  const renderTableRow = (member: MemberResourceStat, index: number) => {
    if (selectedType === 'instance') {
      return (
        <tr key={index} className="border-b border-neutral-100 hover:bg-neutral-50">
          <td className="py-4 px-4 t-sm ">{member.rank}</td>
          <td className="py-4 px-4 t-sb ">{member.id}</td>
          <td className="py-4 px-4 t-sm ">{member.nickname || member.name}</td>
          <td className="py-4 px-4 t-sm ">{getRoleLabel(member.role)}</td>
          <td className="py-4 px-4 t-sm ">{member.instanceNumber}</td>
          <td className="py-4 px-4 text-right t-sm ">{member.cost}</td>
          <td className="py-4 px-4 text-right t-sm ">{member.duration}</td>
        </tr>
      )
    } else if (selectedType === 'snapshot') {
      return (
        <tr key={index} className="border-b border-neutral-100 hover:bg-neutral-50">
          <td className="py-4 px-4 t-sm ">{member.rank}</td>
          <td className="py-4 px-4 t-sm ">{member.id}</td>
          <td className="py-4 px-4 t-sm ">{member.nickname || member.name}</td>
          <td className="py-4 px-4 t-sm ">{getRoleLabel(member.role)}</td>
          <td className="py-4 px-4 t-sm ">{member.snapshotNumber}</td>
          <td className="py-4 px-4 text-right t-sm ">
            <div className="flex items-center gap-1 justify-end">
              <span className="text-primary">{member.storageUsed}</span>
              <span className="text-secondary">
                <Trans i18nKey="teamHome.admin.resources.of">of</Trans>
              </span>
              <span className="text-primary">{member.storageQuota}</span>
            </div>
          </td>
          <td className="py-4 px-4 text-right t-sm ">{member.percentage}</td>
        </tr>
      )
    } else {
      // datadrive
      return (
        <tr key={index} className="border-b border-neutral-100 hover:bg-neutral-50">
          <td className="py-4 px-4 t-sm ">{member.rank}</td>
          <td className="py-4 px-4 t-sm ">{member.id}</td>
          <td className="py-4 px-4 t-sm ">{member.nickname || member.name}</td>
          <td className="py-4 px-4 t-sm ">{getRoleLabel(member.role)}</td>
          <td className="py-4 px-4 t-sm ">
            <div className="flex items-center gap-1">
              {member.region}

              <Tooltip title={t('teamData.admin.regionsTooltip', 'Number of regions')}>
                <InfoIcon className="w-4 h-4 text-tertiary cursor-help" />
              </Tooltip>
            </div>
          </td>
          <td className="py-4 px-4 text-right t-sm">
            <div className="flex items-center gap-1 justify-end">
              <span className="text-primary">{member.storageUsed}</span>
              <span className="text-secondary">
                <Trans i18nKey="teamHome.admin.resources.of">of</Trans>
              </span>
              <span className="text-primary">{member.storageQuota}</span>
            </div>
          </td>
          <td className="py-4 px-4 text-right t-sm ">{member.percentage}</td>
        </tr>
      )
    }
  }

  const renderLoadingSkeleton = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <tr key={index} className="border-b border-neutral-100">
        <td className="py-4 px-4" colSpan={7}>
          <Skeleton active paragraph={false} />
        </td>
      </tr>
    ))
  }

  return (
    <div className="bg-panel-white rounded-2xl p-6 border border-neutral-200">
      {/* Filters */}
      <div className="flex justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-2">
            <span className="t-sb text-secondary">
              <Trans i18nKey="teamHome.admin.resources.filterType">Team Resources Type</Trans>
            </span>
            <ButtonRadio
              items={RESOURCE_TYPES}
              value={selectedType}
              onChange={handleTypeChange}
              size="small"
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="t-sb text-secondary">
              <Trans i18nKey="teamHome.admin.resources.filterSortBy">Sort By</Trans>
            </span>
            <Dropdown
              options={SORT_OPTIONS[selectedType]}
              value={sortBy}
              onChange={(value) => setSortBy(value as string)}
              className="min-w-[180px]"
            />
          </div>
        </div>

        <div className="">
          <Link
            to={RESOURCE_LINKS[selectedType].path}
            className="flex items-center gap-1 t-sm text-secondary pl-3 pr-2 py-2 bg-panel-gray-100 rounded-md"
          >
            {RESOURCE_LINKS[selectedType].label}
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <table className="w-full">
            <thead className="bg-panel-gray-100">{renderTableHeaders()}</thead>
            <tbody>{renderLoadingSkeleton()}</tbody>
          </table>
        ) : sortedTop5Members.length === 0 ? (
          <EmptyView
            title={t('common.emptyViewNoDataMsg', 'No data')}
            content={t(
              'teamHome.admin.resources.emptyContent',
              'No member resource data available',
            )}
          />
        ) : (
          <table className="w-full">
            <thead className="bg-panel-gray-100">{renderTableHeaders()}</thead>
            <tbody>{sortedTop5Members.map((member, index) => renderTableRow(member, index))}</tbody>
          </table>
        )}
      </div>
    </div>
  )
}
